import { useRef, useState } from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * A chip/token list input: type a value, press Enter or comma, get a removable pill.
 *
 * Used for the profile taxonomies (specialties, qualifications, clinical interests, languages),
 * where order matters and the values are short free text rather than a fixed option set — which is
 * why this is not the popover MultiSelect the access-groups picker uses.
 *
 * @param {object} props
 * @param {string[]} props.value
 * @param {(next: string[]) => void} props.onChange
 * @param {'default'|'primary'} [props.variant]
 */
export function TokenField({
  value = [],
  onChange,
  placeholder = 'Add…',
  variant = 'default',
  maxItems = 12,
  maxLength = 60,
  disabled = false,
  id,
}) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const isPrimary = variant === 'primary';

  // Case-insensitive de-dupe: "Cataract" and "cataract" are the same chip to a reader, and two of
  // them in the list would look like a bug rather than a choice.
  const addValues = (raw) => {
    const incoming = raw
      .split(/[,\n]/)
      .map((part) => part.trim().slice(0, maxLength))
      .filter(Boolean);
    if (incoming.length === 0) return;
    const next = [...value];
    for (const item of incoming) {
      if (next.length >= maxItems) break;
      if (next.some((existing) => existing.toLowerCase() === item.toLowerCase())) continue;
      next.push(item);
    }
    if (next.length !== value.length) onChange?.(next);
  };

  const commitDraft = () => {
    if (!draft.trim()) return;
    addValues(draft);
    setDraft('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitDraft();
      return;
    }
    // Backspace on an empty input removes the last token — the standard chip-field affordance.
    if (event.key === 'Backspace' && !draft && value.length > 0) {
      event.preventDefault();
      onChange?.(value.slice(0, -1));
    }
  };

  const atCapacity = value.length >= maxItems;

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-1.5 py-1 text-[13px]',
        'focus-within:outline-none focus-within:ring-2 focus-within:ring-ring',
        disabled && 'cursor-not-allowed opacity-60',
      )}
    >
      {value.map((token) => (
        <span
          key={token}
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[12px] font-bold',
            isPrimary ? 'bg-accent text-primary' : 'bg-muted text-foreground',
          )}
        >
          {token}
          {!disabled ? (
            <button
              type="button"
              aria-label={`Remove ${token}`}
              onClick={(event) => {
                event.stopPropagation();
                onChange?.(value.filter((item) => item !== token));
              }}
              className="grid h-3.5 w-3.5 place-items-center rounded-full opacity-50 transition hover:bg-black/10 hover:opacity-100 dark:hover:bg-white/15"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          ) : null}
        </span>
      ))}
      <input
        id={id}
        ref={inputRef}
        value={draft}
        disabled={disabled || atCapacity}
        maxLength={maxLength}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
        // Commit on blur too: leaving typed text uncommitted silently loses it on save.
        onBlur={commitDraft}
        onPaste={(event) => {
          const text = event.clipboardData.getData('text');
          if (!/[,\n]/.test(text)) return;
          event.preventDefault();
          addValues(text);
        }}
        placeholder={atCapacity ? `Limit of ${maxItems} reached` : placeholder}
        className="h-6 min-w-24 flex-1 border-none bg-transparent px-1 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
      />
    </div>
  );
}
